import { useState } from 'react';
import { api, type LeaderboardItem } from '../lib/api';
import { useFetch } from '../lib/useFetch';
import { Loader } from '../components/Loader';
import { ErrorView } from '../components/ErrorView';
import { PageHeader } from '../components/PageHeader';
import { haptic } from '../lib/telegram';
import { TabGlyph } from '../components/AppIcons';

type Scope = 'all' | 'week';

export function Leaderboard() {
  const [scope, setScope] = useState<Scope>('all');
  const { data, loading, error, reload } = useFetch(() => api.leaderboard(scope, 50), [scope]);

  return (
    <>
      <PageHeader title="Лідерборд" />
      <div className="space-y-4 p-4">
        <div className="flex gap-2">
          <ScopeButton active={scope === 'all'} onClick={() => { setScope('all'); haptic.selection(); }}>
            Всі час
          </ScopeButton>
          <ScopeButton active={scope === 'week'} onClick={() => { setScope('week'); haptic.selection(); }}>
            Цей тиждень
          </ScopeButton>
        </div>

        {loading && <Loader />}
        {error && <ErrorView error={error} onRetry={reload} />}

        {data && (
          <>
            {data.note && <p className="card text-sm text-tg-hint">{data.note}</p>}
            {data.myRank !== null && (
              <p className="card text-sm">
                Ваш ранг: <strong>#{data.myRank}</strong>
              </p>
            )}
            <ol className="space-y-1">
              {data.items.length === 0 && (
                <p className="card text-sm text-tg-hint">Поки що порожньо. Будь першим!</p>
              )}
              {data.items.map((item) => (
                <Row key={item.userId} item={item} />
              ))}
            </ol>
          </>
        )}
      </div>
    </>
  );
}

function ScopeButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium ${
        active ? 'bg-tg-button text-tg-button-text' : 'bg-tg-secondary-bg text-tg-text'
      }`}
    >
      {children}
    </button>
  );
}

function Row({ item }: { item: LeaderboardItem }) {
  const medal = item.rank === 1 ? '🥇' : item.rank === 2 ? '🥈' : item.rank === 3 ? '🥉' : null;
  // Преміум-юзера видно одразу — золота рамка + легке тло, корона на аві
  const cls = item.isPremium
    ? 'card flex items-center gap-3 border-2 border-yellow-500 bg-yellow-500/5'
    : 'card flex items-center gap-3';
  return (
    <li className={cls}>
      <div className="w-8 text-center text-sm font-semibold tabular-nums">
        {medal ?? `#${item.rank}`}
      </div>
      <div className="relative">
        {item.photoUrl ? (
          <img
            src={item.photoUrl}
            alt=""
            className={`h-8 w-8 rounded-full object-cover ${
              item.isPremium ? 'ring-2 ring-yellow-500' : ''
            }`}
          />
        ) : (
          <div
            className={`grid h-8 w-8 place-items-center rounded-full bg-tg-bg text-tg-hint ${
              item.isPremium ? 'ring-2 ring-yellow-500' : ''
            }`}
          >
            <TabGlyph kind="profile" className="h-4 w-4" />
          </div>
        )}
        {item.isPremium && (
          <span className="absolute -right-1 -top-1 text-yellow-500" title="Premium">
            <TabGlyph kind="crown" className="h-3 w-3" />
          </span>
        )}
      </div>
      <div className="flex-1 truncate">
        <div className="flex items-center gap-1.5 text-sm font-medium">
          <span className="truncate">{item.firstName ?? item.username ?? 'Гравець'}</span>
          {item.isPremium && (
            <span className="shrink-0 rounded bg-yellow-500/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-yellow-600">
              Premium
            </span>
          )}
        </div>
        <div className="text-xs text-tg-hint">Рівень {item.level}</div>
      </div>
      <div className="text-right">
        <div className="text-sm font-semibold">{item.xp} XP</div>
      </div>
    </li>
  );
}
